import clsx from "clsx";
import { FieldHint } from "@/components/FieldHint";
import { LicensePlateInput } from "@/components/LicensePlateInput";
import { useLocale } from "@/i18n";
import {
  activeVehicleFields,
  NUMERIC_VEHICLE_FIELD_KEYS,
  vehicleFieldMaxExceededMessage,
  vehicleFieldTooltip,
  vehicleFieldUnit,
  type VehicleFieldFullConfig,
  type VehicleFieldKey,
  type VehicleFormData,
} from "@/lib/vehicleFields";

type Props = {
  form: VehicleFormData;
  config: VehicleFieldFullConfig;
  onChange: (next: VehicleFormData) => void;
};

function placeholderFor(key: VehicleFieldKey, labels: Record<string, string>): string | undefined {
  if (key === "vehicle_length_m") return labels.vehicle_length_placeholder;
  if (key === "tent_size") return labels.tent_size_placeholder;
  return undefined;
}

export function VehicleDetailsFields({ form, config, onChange }: Props) {
  const { messages: m } = useLocale();
  const fields = activeVehicleFields(config);

  if (fields.length === 0) {
    return <p className="text-sm text-slate-500">{m.flow.noVehicleDetailsNeeded}</p>;
  }

  return (
    <section className="bk-section">
      <h3 className="bk-section-title">{m.flow.vehicleDetailsTitle}</h3>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        {fields.map((key) => {
          const required = config.modes[key] === "required";
          const tooltip = vehicleFieldTooltip(config, key);
          const maxExceededMessage = vehicleFieldMaxExceededMessage(config, key, form[key]);
          const unit = vehicleFieldUnit(key);
          const isNumeric = NUMERIC_VEHICLE_FIELD_KEYS.includes(key);
          const hint =
            tooltip ?? (key === "vehicle_length_m" ? m.flow.vehicleLengthHint : null);

          return (
            <label
              key={key}
              className={clsx("bk-label", key === "license_plate" && "sm:col-span-2")}
            >
              {m.vehicleFields[key]}
              {required ? " *" : null}
              {!required ? (
                <span className="bk-label__optional">({m.flow.optionalLabel})</span>
              ) : null}
              {key === "license_plate" ? (
                <LicensePlateInput
                  value={form.license_plate}
                  onChange={(license_plate) => onChange({ ...form, license_plate })}
                  required={required}
                  hintText={tooltip}
                />
              ) : (
                <>
                  <div className="bk-input-group">
                    <input
                      type="text"
                      inputMode={isNumeric ? "decimal" : "text"}
                      className={clsx("bk-input", unit && "bk-input--with-unit")}
                      value={form[key]}
                      onChange={(e) => onChange({ ...form, [key]: e.target.value })}
                      placeholder={placeholderFor(key, m.vehicleFields)}
                      required={required}
                      aria-invalid={maxExceededMessage ? true : undefined}
                    />
                    {unit ? <span className="bk-input-unit">{unit}</span> : null}
                  </div>
                  {hint ? <span className="bk-field-hint block">{hint}</span> : null}
                </>
              )}
              {maxExceededMessage ? <FieldHint>{maxExceededMessage}</FieldHint> : null}
            </label>
          );
        })}
      </div>
    </section>
  );
}
